export default {
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last Updated:',
    lastUpdatedDate: 'October 16, 2026',
    backToHome: '← Back to Home',
    seoDescription:
      'Learn how Geofleet.ai collects, uses, and protects your information, including Google Workspace and Google Sheets integrations, AI/ML limitations, and your privacy rights.',
    intro: [
      'Geofleet.ai ("Geofleet", "we", "us", or "our") respects your privacy and is committed to protecting the information entrusted to us.',
      'This Privacy Policy explains how we collect, use, disclose, retain, and protect information when you access or use the Geofleet.ai website, web applications, mobile applications, APIs, integrations, and related services (collectively, the "Services").',
      'By using the Services, you acknowledge the practices described in this Privacy Policy.',
    ],
    sections: {
      informationWeCollect: {
        title: '1. Information We Collect',
        intro: 'Depending on how you use the Services, we may collect the following categories of information.',
        account: {
          title: '1.1 Account and Profile Information',
          intro: 'This may include:',
          items: [
            'Name',
            'Email address',
            'Phone number',
            'Company or organization name',
            'Job title',
            'Account credentials and authentication information',
            'User preferences',
            'Other information voluntarily provided during account registration or administration',
          ],
        },
        delivery: {
          title: '1.2 Delivery and Operational Information',
          intro: 'Depending on the features used, we may process:',
          items: [
            'Delivery and order information',
            'Customer and recipient information',
            'Delivery addresses',
            'Driver information',
            'Vehicle information',
            'Driver and vehicle locations',
            'Routes and trips',
            'Delivery status',
            'Proof of delivery',
            'Electronic signatures',
            'OTP and verification information',
            'Odometer information',
            'Geofence information',
            'Operational notes and records',
            'Other information submitted by customers or users through the Services',
          ],
        },
        device: {
          title: '1.3 Device and Technical Information',
          intro: 'We may automatically collect:',
          items: [
            'IP address',
            'Browser type',
            'Operating system',
            'Device type',
            'Device identifiers',
            'Log information',
            'Application information',
            'Approximate location derived from technical information where applicable',
            'Usage and interaction information',
          ],
        },
        payment: {
          title: '1.4 Payment and Billing Information',
          intro: 'Where applicable, we may process:',
          items: [
            'Billing information',
            'Subscription information',
            'Transaction information',
            'Payment status',
          ],
          note:
            'Payment card information may be processed directly by third-party payment providers. We do not necessarily store complete payment card information on our systems.',
        },
        communications: {
          title: '1.5 Communications',
          intro: 'We may collect information contained in:',
          items: [
            'Support requests',
            'Emails',
            'Feedback',
            'Service communications',
            'Other communications with Geofleet',
          ],
        },
      },
      googleWorkspace: {
        title: '2. Google Workspace and Google Sheets Data',
        intro: [
          'Geofleet provides an optional integration with Google Workspace that allows authorized users to import information from Google Sheets into Geofleet.',
          'When a user connects a Google account to Geofleet, we access only the Google Workspace information necessary to provide the Google Sheets import functionality requested by the user.',
          'The integration uses the following Google API permissions:',
        ],
        drive: {
          title: 'Google Drive Metadata — drive.metadata.readonly',
          paragraphs: [
            'Geofleet uses Google Drive metadata read-only access to discover and list files available in the user\'s Google Drive, including Google Sheets.',
            'This allows Geofleet to identify information such as:',
          ],
          items: [
            'Spreadsheet name',
            'File ID',
            'File type',
            'Other relevant file metadata necessary to identify the spreadsheet',
          ],
          closing: [
            'This permission is used to allow the user to select the Google Sheet they want to use.',
            'Geofleet does not use this permission to create, modify, or delete files in the user\'s Google Drive.',
          ],
        },
        sheets: {
          title: 'Google Sheets Read-Only — spreadsheets.readonly',
          paragraphs: [
            'After the user selects a spreadsheet, Geofleet uses read-only access to read the contents of the selected Google Sheet and selected sheet/tab.',
            'The information is loaded into the Geofleet import interface so that the user can:',
          ],
          numbered: [
            'Review the spreadsheet information.',
            'Select the appropriate sheet/tab.',
            'Map spreadsheet columns to Geofleet fields.',
            'Complete the requested import.',
          ],
          closing: ['Geofleet does not modify or delete the user\'s Google Sheets data.'],
        },
      },
      googleProcessing: {
        title: '3. Google Workspace Data Processing and Storage',
        paragraphs: [
          'Google Workspace information accessed through the integration is processed solely to provide the user-requested Google Sheets import functionality and related Geofleet functionality.',
          'The source Google spreadsheet and its contents are not retained as a separate copy in Geofleet\'s backend as part of the Google Sheets import process.',
          'Where a user intentionally imports information into Geofleet, the resulting information may become part of the user\'s Geofleet account and may be stored as Geofleet operational data.',
          'For example, if a user imports driver records from a Google Sheet, the resulting driver information may be stored in Geofleet so that the user can use and manage those records through the Services.',
          'Such imported information is handled in accordance with this Privacy Policy and applicable contractual and data-retention requirements.',
          'Geofleet does not use Google Workspace data for advertising, data brokerage, or sale to third parties.',
        ],
      },
      googleLimitedUse: {
        title: '4. Google API Services Limited Use',
        paragraphs: [
          'Geofleet\'s use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.',
          'Geofleet does not sell Google user data.',
          'Google Workspace data obtained through Google APIs is not used to create, train, or improve generalized or foundational artificial intelligence or machine learning models.',
          'Geofleet does not transfer Google Workspace data to third-party artificial intelligence or machine learning services for the purpose of training or improving their models.',
          'Google Workspace data is used only for the user-facing functionality described in this Privacy Policy and for purposes permitted under applicable Google API Services policies.',
        ],
      },
      howWeUse: {
        title: '5. How We Use Information',
        intro: 'We may use information collected through the Services to:',
        items: [
          'Provide and operate the Services.',
          'Create and manage user accounts.',
          'Process orders, deliveries, routes, trips, and operational workflows.',
          'Provide requested integrations and functionality.',
          'Process user-initiated imports.',
          'Provide customer support.',
          'Communicate with users about the Services.',
          'Process subscriptions and payments.',
          'Maintain, secure, and troubleshoot the Services.',
          'Detect and prevent fraud, abuse, and unauthorized access.',
          'Monitor system performance and reliability.',
          'Analyze usage of the Services.',
          'Improve the functionality and user experience of the Services.',
          'Comply with applicable legal obligations.',
          'Enforce our Terms of Service and other applicable agreements.',
        ],
        note:
          'Information obtained through Google Workspace APIs is used only for purposes permitted under the applicable Google API Services User Data Policy and the Limited Use requirements.',
      },
      aiMl: {
        title: '6. Artificial Intelligence and Machine Learning',
        paragraphs: [
          'Geofleet may provide AI-powered, automation, analytics, recommendation, or decision-support functionality as part of the Services.',
          'When you use an AI assistant in the Services, such as the Copilot in GeoFleet Lite, we store your messages and the assistant\'s replies with your organisation\'s account. We use them to show your conversation history on any device, and to improve and train Geofleet\'s AI features. Google Workspace data is never included. Your organisation can ask us to delete this history by contacting support.',
          'Google Workspace data obtained through Google APIs is not used to train, fine-tune, create, or improve generalized or foundational AI/ML models.',
          'Google Workspace data is also not transferred to third-party AI/ML providers for the purpose of training or improving their models.',
          'Any AI or automation functionality involving information obtained through Google Workspace APIs is subject to the limitations and requirements described in this Privacy Policy and applicable Google policies.',
        ],
      },
      sharing: {
        title: '7. Sharing and Disclosure',
        intro: 'We may disclose information in the following circumstances.',
        serviceProviders: {
          title: 'Service Providers',
          paragraphs: [
            'We may use trusted third-party service providers to help us host, operate, secure, maintain, and provide the Services.',
            'These providers may process information on our behalf and are expected to use such information only for the services they provide to Geofleet and subject to appropriate contractual and security obligations.',
          ],
        },
        legal: {
          title: 'Legal Requirements',
          intro: 'We may disclose information where reasonably necessary to:',
          items: [
            'Comply with applicable law or legal process.',
            'Respond to lawful requests from government authorities.',
            'Protect the rights, property, or safety of Geofleet, our users, or others.',
            'Detect, prevent, or investigate fraud or security incidents.',
          ],
        },
        business: {
          title: 'Business Transactions',
          paragraphs: [
            'Information may be transferred as part of a merger, acquisition, financing, restructuring, sale of assets, or similar corporate transaction, subject to applicable legal requirements.',
          ],
        },
        authorization: {
          title: 'With Your Authorization',
          paragraphs: [
            'We may disclose information where you have specifically authorized or instructed us to do so.',
            'We do not sell personal information for third-party marketing purposes.',
            'We do not provide Google Workspace data to third parties for advertising, sale, data brokerage, or training generalized or foundational AI/ML models.',
          ],
        },
      },
      googleAuth: {
        title: '8. Google Authorization and Revocation',
        paragraphs: [
          'When you connect your Google account to Geofleet, you authorize Geofleet to access the Google Workspace information described during Google\'s authorization process.',
          'You may revoke Geofleet\'s Google authorization at any time through your Google Account settings or available Geofleet functionality.',
          'After authorization is revoked, Geofleet will no longer be able to access Google Workspace information using the revoked authorization.',
          'Revoking Google authorization does not automatically delete information that you previously and intentionally imported into Geofleet.',
          'Previously imported Geofleet data remains subject to the applicable Geofleet data retention and deletion procedures.',
        ],
      },
      retention: {
        title: '9. Data Retention',
        intro: 'We retain information for as long as reasonably necessary to:',
        items: [
          'Provide the Services.',
          'Maintain customer accounts.',
          'Fulfill contractual obligations.',
          'Provide customer support.',
          'Meet legal, accounting, or regulatory requirements.',
          'Resolve disputes.',
          'Prevent fraud and abuse.',
          'Enforce our agreements.',
        ],
        paragraphs: [
          'The source Google spreadsheet and its contents are not retained as a separate copy in our backend as part of the Google Sheets import process.',
          'Information intentionally imported into Geofleet may be retained according to the applicable account, subscription, operational, legal, and contractual requirements.',
          'When information is no longer reasonably required, we may delete or anonymize it in accordance with our retention procedures and applicable law.',
        ],
      },
      security: {
        title: '10. Security',
        paragraphs: [
          'We implement reasonable technical and organizational measures designed to protect information against unauthorized access, use, alteration, disclosure, or destruction.',
          'Depending on the applicable service and environment, safeguards may include:',
        ],
        items: [
          'Encryption in transit.',
          'Encryption at rest where applicable.',
          'Access controls.',
          'Authentication mechanisms.',
          'Role-based permissions.',
          'Monitoring and logging.',
          'Infrastructure security controls.',
          'Backup and recovery mechanisms.',
          'Security assessments and operational controls.',
        ],
        note: 'No method of transmission, processing, or storage can be guaranteed to be completely secure.',
      },
      rights: {
        title: '11. Your Rights and Choices',
        intro:
          'Depending on applicable law and your location, you may have rights relating to your personal information, including the right to:',
        items: [
          'Access information we hold about you.',
          'Request correction of inaccurate information.',
          'Request deletion of information.',
          'Request restriction of certain processing.',
          'Object to certain processing.',
          'Request portability of certain information.',
          'Withdraw consent where processing is based on consent.',
          'Lodge a complaint with a relevant data protection authority.',
        ],
        contactIntro: 'To exercise applicable rights or submit a privacy request, contact us at:',
        verify: 'We may need to verify your identity before fulfilling certain requests.',
      },
      cookies: {
        title: '12. Cookies and Similar Technologies',
        intro: 'We and our service providers may use cookies, web beacons, pixels, local storage, and similar technologies to:',
        items: [
          'Operate the Services.',
          'Remember preferences.',
          'Maintain authentication.',
          'Understand usage.',
          'Improve performance.',
          'Detect security issues.',
          'Analyze website traffic.',
        ],
        note:
          'You may control cookies through your browser settings. Disabling certain cookies may affect the availability or functionality of parts of the Services.',
      },
      international: {
        title: '13. International Data Transfers',
        paragraphs: [
          'Geofleet and its service providers may process information in countries other than the country in which you reside.',
          'Where applicable, we take reasonable steps to ensure that international transfers are subject to appropriate safeguards required by applicable law.',
        ],
      },
      children: {
        title: '14. Children\'s Privacy',
        paragraphs: [
          'The Services are intended for business users and are not directed to individuals under 18 years of age.',
          'We do not knowingly collect personal information directly from children under 18.',
          'If you believe a child has provided personal information to us, please contact us at:',
        ],
        closing:
          'We will take reasonable steps to investigate and delete such information where appropriate.',
      },
      thirdParty: {
        title: '15. Third-Party Services',
        paragraphs: [
          'The Services may contain links to or integrations with third-party websites, applications, APIs, and services.',
          'Third-party services are governed by their own terms and privacy policies.',
          'Geofleet is not responsible for the privacy practices of third-party services that are not controlled by Geofleet.',
          'When you enable an integration, you should review the applicable third-party provider\'s privacy policy and terms.',
        ],
      },
      changes: {
        title: '16. Changes to This Privacy Policy',
        paragraphs: [
          'We may update this Privacy Policy periodically to reflect changes in our Services, technology, legal requirements, or business practices.',
          'When we make changes, we will update the "Last Updated" date at the top of this Privacy Policy.',
          'Where appropriate, material changes may also be communicated through email, the Services, or another reasonable method.',
          'Your continued use of the Services after an updated Privacy Policy becomes effective constitutes acknowledgment of the updated policy to the extent permitted by applicable law.',
        ],
      },
      contact: {
        title: '17. Contact Us',
        intro:
          'If you have questions, concerns, or requests relating to this Privacy Policy or our privacy practices, please contact us:',
        company: 'Geofleet.ai',
        emailLabel: 'Email:',
        websiteLabel: 'Website:',
        website: 'https://geofleet.ai',
      },
    },
  },
};
